import React, { memo } from 'react'
import { TableRow, TableCell, Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { DISPLAY } from 'constants/display'
import { ISpotBiddingRule, SPOT_PREMIUM_TYPE, RULE_ACTION } from '../types/spotBiddingConfig'
import {
  formatMileageCriteria,
  formatCountLoadsQuotedCriteria,
  formatDistanceToMarketCenterCriteria,
  formatDateCriteria,
  formatNumDaysOut,
  formatTransitDifferenceCriteria,
} from '../utils/formatters'
import { RuleActions } from './RuleActions'

interface ISpotBiddingRulesTableProps {
  spotBiddingRule: ISpotBiddingRule
  isGlobal?: boolean
  readOnly?: boolean
}

export const formatRule = (spotBiddingRule: ISpotBiddingRule) => {
  const {
    appointmentTimeCriteria,
    carrierWaitTimeCriteria,
    contractCriteria,
    countLoadsQuotedCriteria,
    dateCriteria,
    distanceToMarketCenterCriteria,
    geographicCriteria,
    isBaseline,
    isDrop,
    isMultiStop,
    isSameDay,
    isWeekend,
    laneGeographicCriteria,
    laneTierCriteria,
    mileageCriteria,
    parentEquipmentKey,
    proximalHourCriteria,
    reason,
    ruleAction,
    ruleStartTimeCriteria,
    spotPremium,
    stopSpecificDropCriteria,
    transitDifferenceCriteria,
  } = spotBiddingRule

  const { amount, premiumType } = spotPremium || {}

  const { criteriaType = '', isInbound = '', value: geographicValue = '' } =
    geographicCriteria || {}

  const { deliveryType = '', deliveryValue = '', pickupType = '', pickupValue = '' } =
    laneGeographicCriteria || {}

  const { awardType = '' } = contractCriteria || {}
  const { rangeStart: time = '' } = ruleStartTimeCriteria || {}

  const {
    deliveryType: countLoadsDeliveryType = '',
    deliveryValue: countLoadsDeliveryValue = '',
    isDifferentPickupDelivery: countLoadsDifferentPickupDelivery = false,
    isSamePickupDelivery: countLoadsSamePickupDelivery = false,
    pickupType: countLoadsPickupType = '',
    pickupValue: countLoadsPickupValue = '',
    rangeEnd: countLoadsRangeEnd = '',
    rangeStart: countLoadsRangeStart = '',
  } = countLoadsQuotedCriteria || {}

  const { tier = '' } = laneTierCriteria || {}

  const {
    locationType: dtmcLocationType = '',
    rangeEnd: dtmcRangeEnd = '',
    rangeStart: dtmcRangeStart = '',
  } = distanceToMarketCenterCriteria || {}

  const { rangeEnd = '', rangeStart = '' } = mileageCriteria || {}

  const { timeRangeEnd = '', timeRangeStart = '', timeUnit = '' } = transitDifferenceCriteria || {}

  const { rangeEnd: endDate = '', rangeStart: startDate = '' } = dateCriteria || {}

  const { rangeEnd: hoursOutEnd = '', rangeStart: hoursOutStart = '' } = proximalHourCriteria || {}

  const {
    deliveryRangeEnd = '',
    deliveryRangeStart = '',
    pickupRangeEnd = '',
    pickupRangeStart = '',
  } = appointmentTimeCriteria || {}

  const { rangeEnd: carrierWaitEnd = '', rangeStart: carrierWaitStart = '' } =
    carrierWaitTimeCriteria || {}

  let ruleType = ''
  let type = ''
  let value = ''
  let subtext = ''

  const premium = (() => {
    if (ruleAction === RULE_ACTION.dontBid) {
      return "Don't Bid"
    }
    if (ruleAction === RULE_ACTION.onlyBidOn) {
      return 'Only Bid On'
    }
    if (premiumType === SPOT_PREMIUM_TYPE.percentage) {
      // Don't show decimal place when it's an integer percentage
      const percentage = Number((Number(amount) * 100).toFixed(1))
      return `${percentage}%`
    }
    return amount
  })()

  if (isBaseline) {
    ruleType = 'Baseline'
  } else if (geographicCriteria && proximalHourCriteria) {
    ruleType = isInbound ? 'Inbound (DEL)' : 'Outbound (PU)'
    type = capitalize(criteriaType)
    value = geographicValue
    subtext = formatNumDaysOut(hoursOutStart, hoursOutEnd)
  } else if (geographicCriteria) {
    ruleType = isInbound ? 'Inbound (DEL)' : 'Outbound (PU)'
    type = capitalize(criteriaType)
    value = geographicValue
  } else if (laneGeographicCriteria) {
    ruleType = 'Lane (PU + DEL)'
    type = `${capitalize(pickupType)} - ${capitalize(deliveryType)}`
    value = `${pickupValue} - ${deliveryValue}`
  } else if (contractCriteria) {
    ruleType = 'Contract'
    type = 'Award Type'
    value = capitalize(awardType)
  } else if (countLoadsQuotedCriteria) {
    ruleType = 'Step Pricing'
    type = `${capitalize(countLoadsPickupType)} - ${capitalize(countLoadsDeliveryType)}`
    value = formatCountLoadsQuotedCriteria(
      countLoadsPickupValue,
      countLoadsDeliveryValue,
      countLoadsRangeStart,
      countLoadsRangeEnd,
      countLoadsSamePickupDelivery,
      countLoadsDifferentPickupDelivery
    )
  } else if (laneTierCriteria) {
    ruleType = 'Lane Tier'
    value = tier
  } else if (distanceToMarketCenterCriteria) {
    ruleType = 'Distance to Market Center'
    type = capitalize(dtmcLocationType)
    value = formatDistanceToMarketCenterCriteria(dtmcRangeStart, dtmcRangeEnd)
  } else if (mileageCriteria && appointmentTimeCriteria) {
    ruleType = `Appointment Time ${pickupRangeStart ? '(PU)' : '(DEL)'}`
    type = rangeEnd ? 'Local' : 'Non Local'
    value = pickupRangeStart
      ? `${pickupRangeStart}-${pickupRangeEnd}`
      : `${deliveryRangeStart}-${deliveryRangeEnd}`
  } else if (mileageCriteria) {
    ruleType = 'Mileage'
    value = formatMileageCriteria(rangeStart, rangeEnd)
    if (isDrop) {
      value += ' (drop)'
    }
  } else if (transitDifferenceCriteria) {
    ruleType = 'Transit Difference'
    value = formatTransitDifferenceCriteria(timeUnit, timeRangeStart, timeRangeEnd)
    if (isMultiStop) {
      value += ' (multistop)'
    }
  } else if (isSameDay || isWeekend || dateCriteria || proximalHourCriteria) {
    ruleType = 'Date'
    if (isSameDay) {
      type = 'Same Day'
      if (isDrop) {
        value = 'Drop'
      }
      if (proximalHourCriteria) {
        value = formatNumDaysOut(hoursOutStart, hoursOutEnd)
      }
      if (ruleStartTimeCriteria) {
        value += ` (Rule starts - ${time})`
      }
    } else if (isWeekend) {
      type = 'Weekend'
    } else if (dateCriteria) {
      type = 'Specific Date/Range'
      value = formatDateCriteria(startDate, endDate)
    } else if (proximalHourCriteria) {
      type = 'Number of Day(s) Out'
      value = formatNumDaysOut(hoursOutStart, hoursOutEnd)
      if (isDrop) {
        value += ' (drop)'
      }
      if (isSameDay) {
        value += ' (same day)'
      }
    }
  } else if (parentEquipmentKey) {
    ruleType = 'Equipment'
    value = capitalize(parentEquipmentKey.split('_').join(' '))
  } else if (carrierWaitTimeCriteria) {
    ruleType = 'Excess Transit'
    if (carrierWaitStart && carrierWaitEnd) {
      value = `${Number(carrierWaitStart)} to ${Number(carrierWaitEnd)} hours`
    } else if (carrierWaitStart) {
      value = `${Number(carrierWaitStart)} or more hours`
    } else {
      value = `< ${Number(carrierWaitEnd)} hours`
    }
  } else if (isDrop) {
    ruleType = 'Drop Only'
    if (stopSpecificDropCriteria) {
      const { deliveryDropTrailer: delivery, pickupDropTrailer: pickup } = stopSpecificDropCriteria
      type = 'Stop Specific Drop'
      value = (() => {
        if (pickup && delivery) {
          return 'Pickup & Delivery'
        }
        if (pickup) {
          return 'Pickup'
        }
        if (delivery) {
          return 'Delivery'
        }
        return ''
      })()
    }
  }

  return { ruleType, type, value, premium, subtext, reason }
}

export const SpotBiddingRulesTableRow = memo<ISpotBiddingRulesTableProps>(
  ({ isGlobal, readOnly, spotBiddingRule }) => {
    const { premium, reason, ruleType, subtext, type, value } = formatRule(spotBiddingRule)

    return (
      <TableRow
        dataTest={`spot-bidding-rule-${spotBiddingRule.id}`}
        className={isGlobal ? 'global-rule' : ''}>
        <TableCell>{ruleType}</TableCell>
        <TableCell>{type}</TableCell>
        <TableCell>
          {value}
          {subtext && <p className='text-small'>{subtext}</p>}
        </TableCell>
        <TableCell type='number'>{premium || ''}</TableCell>
        <TableCell className='reason-cell'>
          {' '}
          <span title={reason}> {reason || DISPLAY.empty} </span>{' '}
        </TableCell>
        {!readOnly && (
          <TableCell>
            <div className='fr'>
              {isGlobal ? (
                <Tag label='Global' size={TAG_SIZE.small} tagStyle={TAG_STYLE.info} />
              ) : (
                <RuleActions rule={spotBiddingRule} />
              )}
            </div>
          </TableCell>
        )}
      </TableRow>
    )
  }
)
